import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * Adding a kind of problem to the directory.
 *
 * Only the name is required. A crew meeting something new in the field should
 * be able to get it into the list in one field and write the client-facing
 * prose later from a desk — demanding a polished description at the moment
 * somebody is trying to record a tree is how a directory ends up with
 * "mistletoe??" as its permanent explanation.
 *
 * The two long fields are generous at 4000 characters because both reach a
 * proposal, where there is room to make an argument, unlike a note on a pin.
 */
export const createTreeProblemTypeInputSchema = z
  .object({
    name: z.string().trim().min(1, 'A problem needs a name').max(80),
    description: optionalText(4000),
    solution: optionalText(4000),
    /** An already-uploaded document to use as the stock photograph. */
    photoDocumentId: uuidSchema.nullish(),
    /** An already-reserved video to use as the explanatory clip. */
    videoId: uuidSchema.nullish(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .strict()

export type CreateTreeProblemTypeInput = z.infer<
  typeof createTreeProblemTypeInputSchema
>

/** Every field optional; absent means "leave it alone", null clears it. */
export const updateTreeProblemTypeInputSchema =
  createTreeProblemTypeInputSchema.partial()

export type UpdateTreeProblemTypeInput = z.infer<
  typeof updateTreeProblemTypeInputSchema
>
